import React, {useEffect, useMemo, useState} from 'react';
import {RestaurantSection, TablesTabProps} from '../../dashboard/types';
import {requestDashboard} from '../../../api/api';

export const TablesTab: React.FC<TablesTabProps> = ({tables, apiBase, token, onAddTable, onEditTable}) => {
  const [sections, setSections] = useState<RestaurantSection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [showTableForm, setShowTableForm] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const [sectionForm, setSectionForm] = useState({name: '', photos: '', description: ''});
  const [tableForm, setTableForm] = useState({tableNumber: 1, capacity: 2, sectionId: '', description: ''});
  const [message, setMessage] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const sectionPhotos = sectionForm.photos.split('\n').map((url) => url.trim()).filter(Boolean);
  const [isError, setIsError] = useState(false);

  const loadSections = async () => {
    try {
      const data = await requestDashboard<RestaurantSection[]>({apiBase, token, path: '/tables/sections', withAuth: true});
      setSections(data);
      if (!selectedSectionId && data.length) setSelectedSectionId(data[0].id);
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : 'Unable to load restaurant sections.');
    }
  };

  useEffect(() => { loadSections(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleTables = useMemo(() => tables.filter((table) => !selectedSectionId || table.sectionId === selectedSectionId), [tables, selectedSectionId]);
  const selectedSection = sections.find((section) => section.id === selectedSectionId);

  const selectSection = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    const numbers = tables.filter((table) => table.sectionId === sectionId).map((table) => Number(table.tableNumber));
    setTableForm((current) => ({...current, sectionId, tableNumber: Math.max(0, ...numbers) + 1}));
  };

  const uploadPhotos = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (sectionPhotos.length + files.length > 12) {
      setIsError(true);
      setMessage('Each section can have up to 12 photos.');
      return;
    }
    if (files.some((file) => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024)) {
      setIsError(true);
      setMessage('Choose JPEG, PNG, or WebP images up to 5 MB each.');
      return;
    }
    setUploadingPhotos(true);
    setIsError(false);
    setMessage('Uploading photos…');
    try {
      for (const file of files) {
        const body = new FormData();
        body.append('file', file);
        const result = await requestDashboard<{url: string}>({apiBase, token, path: '/tables/sections/images', withAuth: true, init: {method: 'POST', body}});
        setSectionForm((current) => ({...current, photos: [current.photos.trim(), result.url].filter(Boolean).join('\n')}));
      }
      setMessage('Photos uploaded. Save the section to apply your changes.');
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : 'Unable to upload photos. Please try again.');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const saveSection = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const imageUrls = Array.from(new Set(sectionForm.photos.split('\n').map((url) => url.trim()).filter(Boolean)));
      const payload = {name: sectionForm.name, imageUrls, imageUrl: imageUrls[0] || null, description: sectionForm.description || null};
      const saved = await requestDashboard<RestaurantSection>({apiBase, token, path: editingSectionId ? `/tables/sections/${editingSectionId}` : '/tables/sections', withAuth: true, init: {method: editingSectionId ? 'PATCH' : 'POST', body: JSON.stringify(payload)}});
      setSections((current) => editingSectionId ? current.map((section) => section.id === saved.id ? saved : section).sort((a, b) => a.name.localeCompare(b.name)) : [...current, saved].sort((a, b) => a.name.localeCompare(b.name)));
      selectSection(saved.id);
      setSectionForm({name: '', photos: '', description: ''});
      setEditingSectionId(null);
      setShowSectionForm(false);
      setIsError(false);
      setMessage(editingSectionId ? 'Section updated successfully.' : 'Section added successfully.');
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : 'Unable to save section.');
    }
  };

  const startEditingSection = (section: RestaurantSection) => {
    setEditingSectionId(section.id);
    setSectionForm({name: section.name, photos: (section.imageUrls?.length ? section.imageUrls : section.imageUrl ? [section.imageUrl] : []).join('\n'), description: section.description || ''});
    setShowSectionForm(true);
  };

  const closeSectionForm = () => {
    setEditingSectionId(null);
    setSectionForm({name: '', photos: '', description: ''});
    setShowSectionForm(false);
  };

  const saveTable = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload = {...tableForm, description: tableForm.description || null};
    const saved = editingTableId ? await onEditTable(editingTableId, payload) : await onAddTable(payload);
    if (saved) {
      setShowTableForm(false);
      setEditingTableId(null);
      setTableForm((current) => ({...current, tableNumber: current.tableNumber + 1, description: ''}));
      await loadSections();
    }
  };

  const startEditingTable = (table: typeof tables[number]) => {
    setEditingTableId(table.id);
    setTableForm({tableNumber: Number(table.tableNumber), capacity: table.capacity, sectionId: table.sectionId, description: table.description || ''});
    setShowTableForm(true);
  };

  const closeTableForm = () => {
    setEditingTableId(null);
    setShowTableForm(false);
  };

  return <section className='sections-admin'>
    <header className='sections-admin-header'>
      <div><p className='eyebrow'>Floor organization</p><h2>Restaurant Sections & Tables</h2></div>
      <button type='button' className='primary-action' disabled={uploadingPhotos} onClick={() => showSectionForm ? closeSectionForm() : setShowSectionForm(true)}>{showSectionForm ? 'Cancel' : 'Add Section'}</button>
    </header>
    {message && <p className={`status ${isError ? 'error' : 'success'}`}>{message}</p>}
    {showSectionForm && <form className='section-create-form' onSubmit={saveSection}>
      <fieldset disabled={uploadingPhotos} className='section-photo-fields'>
      <label>Section name<input value={sectionForm.name} onChange={(event) => setSectionForm({...sectionForm, name: event.target.value})} placeholder='e.g. Balcony' required /></label>
      <div className='section-photo-editor'>
        <label>Upload section photos<input type='file' accept='image/jpeg,image/png,image/webp' multiple onChange={uploadPhotos} /></label>
        <small>Up to 12 photos, 5 MB each. JPEG, PNG, or WebP. The first photo is the cover.</small>
        {uploadingPhotos && <p role='status'>Uploading photos…</p>}
        <div className='section-photo-previews'>
          {sectionPhotos.map((url, index) => <div key={`${index}-${url}`}>
            <img src={url} alt={`Section photo ${index + 1}`} />
            <button type='button' onClick={() => setSectionForm((current) => ({...current, photos: sectionPhotos.filter((_, photoIndex) => photoIndex !== index).join('\n')}))}>Remove photo {index + 1}</button>
          </div>)}
        </div>
      </div>
      <label>Photo URLs (one per line, up to 12)<textarea rows={4} value={sectionForm.photos} onChange={(event) => setSectionForm({...sectionForm, photos: event.target.value})} placeholder='https://…' /></label>
      <label className='section-description'>Description<input value={sectionForm.description} onChange={(event) => setSectionForm({...sectionForm, description: event.target.value})} placeholder='Optional description' /></label>
      <button type='submit' className='primary-action'>{editingSectionId ? 'Update Section' : 'Save Section'}</button>
      </fieldset>
    </form>}
    <div className='section-card-grid'>
      {sections.map((section) => <button type='button' key={section.id} className={`section-card ${selectedSectionId === section.id ? 'active' : ''}`} onClick={() => selectSection(section.id)}>
        {(section.imageUrls?.[0] || section.imageUrl) ? <img src={section.imageUrls?.[0] || section.imageUrl || ''} alt='' /> : <div className='section-image-placeholder'>Section</div>}
        <span><strong>{section.name}</strong><small>{tables.filter((table) => table.sectionId === section.id).length} tables</small></span>
      </button>)}
      {!sections.length && <p className='empty-copy'>Add the first restaurant section to start creating tables.</p>}
    </div>
    {selectedSection && <div className='section-tables-panel'>
      <div className='section-tables-heading'><div><h3>{selectedSection.name}</h3><p>{selectedSection.description || 'Tables in this section'}</p></div><div className='flex gap-2'><button type='button' disabled={uploadingPhotos} onClick={() => startEditingSection(selectedSection)}>Edit Section</button><button type='button' className='primary-action' onClick={() => { if (showTableForm) { closeTableForm(); } else { setEditingTableId(null); setTableForm((current) => ({...current, sectionId: selectedSection.id})); setShowTableForm(true); } }}>{showTableForm ? 'Cancel' : 'Add Table'}</button></div></div>
      {showTableForm && <form className='table-create-form' onSubmit={saveTable}>
        <label>Table number<input type='number' min='1' value={tableForm.tableNumber} onChange={(event) => setTableForm({...tableForm, tableNumber: Number(event.target.value)})} required /></label>
        <label>Seating capacity<input type='number' min='1' value={tableForm.capacity} onChange={(event) => setTableForm({...tableForm, capacity: Number(event.target.value)})} required /></label>
        <label>Section<select value={tableForm.sectionId} onChange={(event) => setTableForm({...tableForm, sectionId: event.target.value})} required>{sections.map((section) => <option key={section.id} value={section.id}>{section.name}</option>)}</select></label>
        <label>Description<input value={tableForm.description} onChange={(event) => setTableForm({...tableForm, description: event.target.value})} placeholder='Optional' /></label>
        <button type='submit' className='primary-action'>{editingTableId ? 'Update Table' : 'Save Table'}</button>
      </form>}
      <div className='table-wrap'><table><thead><tr><th>Table</th><th>Capacity</th><th>Description</th><th>Status</th><th>Action</th></tr></thead><tbody>
        {visibleTables.map((table) => <tr key={table.id}><td>Table {table.tableNumber}</td><td>{table.capacity} seats</td><td>{table.description || '—'}</td><td>{table.reserved ? 'Reserved' : 'Available'}</td><td><button type='button' onClick={() => startEditingTable(table)}>Edit</button></td></tr>)}
        {!visibleTables.length && <tr><td colSpan={5} className='empty-row'>No tables in this section yet.</td></tr>}
      </tbody></table></div>
    </div>}
  </section>;
};
